export const PRICING_TYPE_OPTIONS = [
  { value: 'no_public_pricing', label: 'No public pricing' },
  { value: 'free', label: 'Free only' },
  { value: 'free_trial', label: 'Free trial' },
  { value: 'freemium', label: 'Freemium' },
  { value: 'paid', label: 'Paid only' },
] as const;

export const BILLING_MODEL_OPTIONS = [
  { value: 'subscription', label: 'Subscription' },
  { value: 'one_time', label: 'One-time purchase' },
  { value: 'usage_based', label: 'Usage based' },
  { value: 'credits', label: 'Credits' },
  { value: 'contact_sales', label: 'Contact sales' },
  { value: 'other', label: 'Other' },
] as const;

export const BILLING_PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
] as const;

export const PRICE_BASIS_OPTIONS = [
  { value: 'plan_start', label: 'Plan starting price' },
  { value: 'minimum_top_up', label: 'Minimum top-up' },
  { value: 'per_unit', label: 'Per unit' },
] as const;

// Suggestions for the submit form. Validation accepts any well-formed
// three-letter currency code so products are not forced into this shortlist.
export const PRICE_CURRENCY_OPTIONS = [
  'USD', 'EUR', 'GBP', 'CNY', 'JPY', 'KRW', 'RUB', 'CAD', 'AUD', 'INR', 'BRL',
  'CHF', 'SGD', 'HKD', 'TWD', 'NZD', 'SEK', 'NOK', 'DKK', 'AED', 'SAR', 'ZAR',
  'MXN',
] as const;

export const SUBMISSION_LOCALES = [
  'en', 'zh', 'ko', 'ja', 'de', 'es', 'fr', 'ru',
] as const;

export type PricingType = typeof PRICING_TYPE_OPTIONS[number]['value'];
export type BillingModel = typeof BILLING_MODEL_OPTIONS[number]['value'];
export type BillingPeriod = typeof BILLING_PERIOD_OPTIONS[number]['value'];
export type PriceBasis = 'plan_start' | 'one_time' | 'minimum_top_up' | 'per_unit';

const pricingTypes = new Set<string>(PRICING_TYPE_OPTIONS.map((option) => option.value));
const billingModels = new Set<string>(BILLING_MODEL_OPTIONS.map((option) => option.value));
const billingPeriods = new Set<string>(BILLING_PERIOD_OPTIONS.map((option) => option.value));
const selectablePriceBases = new Set<string>(PRICE_BASIS_OPTIONS.map((option) => option.value));
const submissionLocales = new Set<string>(SUBMISSION_LOCALES);

function normalizedText(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

export function splitSuggestedValues(value: unknown, limit: number) {
  const source = Array.isArray(value) ? value : normalizedText(value).split(/[\n,;]+/u);
  const values: string[] = [];
  const seen = new Set<string>();

  for (const item of source) {
    const text = normalizedText(item).replace(/\s+/gu, ' ');
    const key = text.toLocaleLowerCase();
    if (!text || text.length > 80 || seen.has(key)) continue;
    seen.add(key);
    values.push(text);
    if (values.length >= limit) break;
  }

  return values;
}

export function parseStartingPrice(value: unknown) {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0 || value > 9999999999.99) return null;
    const rounded = Math.round(value * 100) / 100;
    return Math.abs(value - rounded) <= Number.EPSILON * Math.max(1, Math.abs(value)) * 4
      ? rounded
      : null;
  }
  const text = normalizedText(value).replace(',', '.');
  if (!text) return null;
  if (!/^\d+(?:\.\d{1,2})?$/u.test(text)) return null;
  const amount = Number(text);
  return Number.isFinite(amount) && amount <= 9999999999.99 ? amount : null;
}

export function isPriceCurrencyCode(value: unknown) {
  return /^[A-Z]{3}$/u.test(normalizedText(value).toUpperCase());
}

type SubmissionDetailsInput = {
  pricingType?: unknown;
  billingModel?: unknown;
  billingPeriod?: unknown;
  startingPrice?: unknown;
  priceCurrency?: unknown;
  priceBasis?: unknown;
  priceUnit?: unknown;
  suggestedKeywords?: unknown;
  submissionLocale?: unknown;
  productBrief?: unknown;
};

export type SubmissionPricingValidationIssue = {
  field: 'pricingType' | 'billingModel' | 'billingPeriod' | 'startingPrice' | 'priceCurrency' | 'priceBasis' | 'priceUnit';
  message: string;
};

export function validateSubmissionPricingDetails(
  input: SubmissionDetailsInput,
): SubmissionPricingValidationIssue[] {
  const pricingType = normalizedText(input.pricingType);
  const billingModel = normalizedText(input.billingModel);
  const billingPeriod = normalizedText(input.billingPeriod);
  const priceBasis = normalizedText(input.priceBasis);
  const issues: SubmissionPricingValidationIssue[] = [];

  if (!pricingTypes.has(pricingType)) {
    issues.push({ field: 'pricingType', message: 'Select the current pricing status' });
    return issues;
  }

  const hasPaidPricing = pricingType === 'free_trial'
    || pricingType === 'freemium'
    || pricingType === 'paid';
  if (!hasPaidPricing) return issues;

  if (!billingModels.has(billingModel)) {
    issues.push({ field: 'billingModel', message: 'Select the charging model' });
    return issues;
  }
  if (billingModel === 'subscription' && !billingPeriods.has(billingPeriod)) {
    issues.push({ field: 'billingPeriod', message: 'Select monthly or yearly billing' });
  }
  if (billingModel !== 'contact_sales') {
    if (parseStartingPrice(input.startingPrice) === null) {
      issues.push({ field: 'startingPrice', message: 'Enter the current starting price' });
    }
    const currency = normalizedText(input.priceCurrency).toUpperCase();
    if (!isPriceCurrencyCode(currency)) {
      issues.push({ field: 'priceCurrency', message: 'Enter a three-letter currency code' });
    }
    if (['usage_based', 'credits', 'other'].includes(billingModel)
      && !selectablePriceBases.has(priceBasis)) {
      issues.push({ field: 'priceBasis', message: 'Select what the amount represents' });
    }
    if (['usage_based', 'credits', 'other'].includes(billingModel)
      && priceBasis === 'per_unit'
      && !normalizedText(input.priceUnit)) {
      issues.push({ field: 'priceUnit', message: 'Enter the billing unit' });
    }
  }
  return issues;
}

export function buildSubmissionDetails(
  input: SubmissionDetailsInput,
  declaredAt = new Date().toISOString(),
) {
  const pricingType = normalizedText(input.pricingType);
  const billingModel = normalizedText(input.billingModel);
  const billingPeriod = normalizedText(input.billingPeriod);
  const currency = normalizedText(input.priceCurrency).toUpperCase();
  const startingPrice = parseStartingPrice(input.startingPrice);
  const submittedPriceBasis = normalizedText(input.priceBasis);
  const priceUnit = Array.from(normalizedText(input.priceUnit)).slice(0, 40).join('');
  const suggestedKeywords = splitSuggestedValues(input.suggestedKeywords, 8);
  const submissionLocale = normalizedText(input.submissionLocale).toLocaleLowerCase();
  const productBrief = Array.from(normalizedText(input.productBrief)).slice(0, 2000).join('');
  const normalizedPricingType = pricingTypes.has(pricingType) ? pricingType as PricingType : null;
  const permitsPaidDetails = normalizedPricingType === 'free_trial'
    || normalizedPricingType === 'freemium'
    || normalizedPricingType === 'paid';
  const permitsAmountDetails = permitsPaidDetails && billingModel !== 'contact_sales';
  const priceBasis: PriceBasis | null = !permitsPaidDetails || billingModel === 'contact_sales'
    ? null
    : billingModel === 'subscription'
      ? 'plan_start'
      : billingModel === 'one_time'
        ? 'one_time'
        : selectablePriceBases.has(submittedPriceBasis)
          ? submittedPriceBasis as PriceBasis
          : null;

  return {
    ...(normalizedPricingType ? {
      pricing_type: normalizedPricingType,
      billing_model: permitsPaidDetails && billingModels.has(billingModel)
        ? billingModel as BillingModel
        : null,
      billing_period: permitsPaidDetails
        && billingModel === 'subscription'
        && billingPeriods.has(billingPeriod)
        ? billingPeriod as BillingPeriod
        : null,
      starting_price: permitsAmountDetails ? startingPrice : null,
      price_currency: permitsAmountDetails && startingPrice !== null && isPriceCurrencyCode(currency)
        ? currency
        : null,
      price_basis: priceBasis,
      price_unit: priceBasis === 'per_unit' && priceUnit ? priceUnit : null,
      pricing_declared_at: declaredAt,
    } : {}),
    ...(suggestedKeywords.length > 0 ? { suggested_keywords: suggestedKeywords } : {}),
    ...(submissionLocales.has(submissionLocale) ? { submission_locale: submissionLocale } : {}),
    product_brief: productBrief || null,
  };
}
